"use client";

import { supabase } from "@/lib/supabase";
import type { DbTeam } from "@/types/db";
import { useCallback, useEffect, useState } from "react";

type MembershipRow = {
  team_id: string;
  role: string;
};

export type UseTeamsReturn = {
  teams: DbTeam[];
  /** ID of the user's personal (home) team, or first available team. Null while loading. */
  personalTeamId: string | null;
  loading: boolean;
  refresh: () => void;
};

export function useTeams(): UseTeamsReturn {
  const [teams, setTeams] = useState<DbTeam[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchTeams = useCallback(async () => {
    setLoading(true);
    const { data: { session } } = await supabase.auth.getSession();
    const user = session?.user;

    if (!user) {
      setTeams([]);
      setLoading(false);
      return;
    }

    // Fetch all teams accessible to the user. RLS returns:
    //   - teams the user owns (teams.user_id = user.id)
    //   - shared teams the user is a member of (via team_members)
    const [teamsResult, membershipsResult] = await Promise.all([
      supabase
        .from("teams")
        .select("id, user_id, name, description, icon_type, is_home, team_type, created_at, updated_at")
        .order("is_home", { ascending: false })
        .order("name", { ascending: true }),
      supabase
        .from("team_members")
        .select("team_id, role")
        .eq("user_id", user.id),
    ]);

    if (teamsResult.error) {
      console.error("[useTeams] fetch error:", teamsResult.error);
    }

    const roleByTeamId = new Map<string, string>(
      ((membershipsResult.data as MembershipRow[] | null) ?? []).map((m) => [
        m.team_id,
        m.role,
      ])
    );

    const teamsWithRole: DbTeam[] = (
      (teamsResult.data as Omit<DbTeam, "currentUserRole">[] | null) ?? []
    ).map((team) => ({
      ...team,
      // Owned teams: user is always team_owner regardless of team_members row.
      // Member teams: use the role stored in team_members.
      currentUserRole:
        team.user_id === user.id
          ? "team_owner"
          : (roleByTeamId.get(team.id) ?? null),
    }));

    // Permission boundary: users should never see another user's personal space.
    // Keep shared/non-home teams plus only the current user's home team.
    const permittedTeams = teamsWithRole.filter(
      (team) => !team.is_home || team.user_id === user.id
    );

    setTeams(permittedTeams);
    setLoading(false);
  }, []);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    fetchTeams();
  }, [fetchTeams]);

  // Prefer the user's own home team; fall back to first owned team, then any team.
  const personalTeamId = (() => {
    const ownedHome = teams.find(
      (t) => t.is_home && t.currentUserRole === "team_owner"
    );
    if (ownedHome) return ownedHome.id;
    const firstOwned = teams.find((t) => t.currentUserRole === "team_owner");
    if (firstOwned) return firstOwned.id;
    return teams[0]?.id ?? null;
  })();

  return { teams, personalTeamId, loading, refresh: fetchTeams };
}
